import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { criarFases } from '../data/fases';
import { criarFasesCipamin } from '../data/fasesCipamin';
import { criarFasesCipatr } from '../data/fasesCipatr';
import { Fase } from '../types';
import { Users, FileText, Vote, CheckCircle, Award, Calendar } from 'lucide-react';
import { getTipoComissao } from '../utils/cipaminUtils';

// Função para obter o nome do ícone a partir do componente
const getIconName = (iconComponent: React.ComponentType<any>): string => {
  if (iconComponent === Users) return 'Users';
  if (iconComponent === FileText) return 'FileText';
  if (iconComponent === Vote) return 'Vote';
  if (iconComponent === CheckCircle) return 'CheckCircle';
  if (iconComponent === Award) return 'Award';
  if (iconComponent === Calendar) return 'Calendar';
  return 'Calendar'; // fallback
};

interface AcaoCronograma {
  id: string;
  fase_id: string;
  usuario_id: string;
  empresa_id?: string;
  descricao: string;
  obrigatoria: boolean;
  concluida: boolean;
  data_conclusao?: string;
  ordem: number;
}

interface FaseComAcoes extends Fase {
  acoes_banco: AcaoCronograma[];
}

export const useCronograma = () => {
  const { user, empresaSelecionada } = useAuth();
  const [fases, setFases] = useState<FaseComAcoes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataTerminoMandato, setDataTerminoMandato] = useState<string>('');
  const lastLoadRef = useRef<string>('');

  // Criar fases padrão para novos usuários
  const criarFasesPadrao = useCallback(async (novaDataTermino?: string) => {
    if (!user) return;

    try {
      console.log('🔄 [useCronograma] Criando fases padrão para o usuário...');
      console.log('📊 [useCronograma] Dados do usuário:', { id: user.id, nome: user.nome_completo });
      console.log('📊 [useCronograma] Empresa selecionada:', empresaSelecionada?.nome, 'ID:', empresaSelecionada?.id);
      console.log('📊 [useCronograma] Empresa completa:', empresaSelecionada);
      console.log('📊 [useCronograma] tipo_comissao do banco:', (empresaSelecionada as any)?.tipo_comissao);
      console.log('📊 [useCronograma] cnae_principal:', (empresaSelecionada as any)?.cnae_principal);
      
      // Detectar tipo de comissão (CIPA ou CIPAMIN)
      const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
      const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
      const nomeEmpresa = (empresaSelecionada as any)?.nome;
      const tipoComissaoCalculado = getTipoComissao(cnaeEmpresa, nomeEmpresa);
      const tipoComissao = tipoComissaoBanco || tipoComissaoCalculado;
      
      console.log(`🏗️ [useCronograma] Tipo de comissão detectado: ${tipoComissao}`);
      console.log(`🏗️ [useCronograma] - Fonte: ${tipoComissaoBanco ? 'banco de dados' : (cnaeEmpresa ? 'calculado pelo CNAE' : 'calculado pelo nome da empresa')}`);
      
      // Primeiro, limpar fases existentes da empresa específica
      let deleteFasesQuery = supabase
        .from('cp_fases')
        .delete()
        .eq('usuario_id', user.id);
      
      let deleteAcoesQuery = supabase
        .from('cp_acoes_cronograma')
        .delete()
        .eq('usuario_id', user.id);

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== 'SUPER_ADMIN') {
        console.log('🗑️ Deletando fases da empresa:', empresaSelecionada.nome);
        deleteFasesQuery = deleteFasesQuery.eq('empresa_id', empresaSelecionada.id);
        deleteAcoesQuery = deleteAcoesQuery.eq('empresa_id', empresaSelecionada.id);
      } else {
        console.log('🗑️ Deletando todas as fases do usuário');
      }

      await deleteFasesQuery;
      await deleteAcoesQuery;
      
      // Usar a nova data se fornecida, senão usar data da empresa selecionada ou do usuário como fallback
      const dataTermino = novaDataTermino || empresaSelecionada?.data_termino_mandato || user.data_termino_mandato || '2025-09-26';
      console.log('📅 Data de término do mandato:', dataTermino);
      console.log('📅 Fonte da data:', novaDataTermino ? 'nova data fornecida' : (empresaSelecionada?.data_termino_mandato ? 'empresa' : 'usuário'));
      
      // Definir a data de término no estado
      setDataTerminoMandato(dataTermino);
      
      // Criar fases baseado no tipo de comissão
      let fasesDefault: Fase[];
      if (tipoComissao === 'CIPAMIN') {
        fasesDefault = criarFasesCipamin(dataTermino);
      } else if (tipoComissao === 'CIPATR') {
        fasesDefault = criarFasesCipatr(dataTermino);
      } else {
        fasesDefault = criarFases(dataTermino);
      }
      
      console.log(`📋 Fases ${tipoComissao} criadas:`, fasesDefault.length);
      
      // Preparar dados para batch insert
      const fasesParaInserir = fasesDefault.map((fase, i) => {
        console.log(`📝 Fase ${i + 1}: ${fase.titulo} - ${fase.data.toISOString().split('T')[0]}`);
        return {
          usuario_id: user.id,
          empresa_id: empresaSelecionada?.id || null,
          fase_tipo: fase.id,
          titulo: fase.titulo,
          data_planejada: fase.data,
          data_fim: fase.dataFim,
          artigo_nr: fase.artigo,
          prazo_nr: fase.prazoNR,
          cor: fase.cor,
          icon: getIconName(fase.icon),
          ordem: i + 1,
          tipo_comissao: tipoComissao
        };
      });

      console.log('💾 Inserindo fases no banco de dados...');
      // Inserir todas as fases de uma vez
      const { data: fasesInseridas, error: fasesError } = await supabase
        .from('cp_fases')
        .insert(fasesParaInserir)
        .select();

      if (fasesError) {
        console.error('❌ Erro ao criar fases:', fasesError);
        throw fasesError;
      }
      console.log('✅ Fases inseridas com sucesso:', fasesInseridas?.length);

      // Preparar e inserir todas as ações de uma vez
      const acoesParaInserir: any[] = [];
      
      fasesInseridas?.forEach((faseInserida, index) => {
        const fase = fasesDefault[index];
        if (fase.acoes && fase.acoes.length > 0) {
          console.log(`📝 Ações da fase ${fase.titulo}:`, fase.acoes.length);
          const acoes = fase.acoes.map((acao, acaoIndex) => ({
            fase_id: faseInserida.id,
            usuario_id: user.id,
            empresa_id: empresaSelecionada?.id || null,
            descricao: acao,
            obrigatoria: true,
            ordem: acaoIndex + 1,
            tipo_comissao: tipoComissao
          }));
          acoesParaInserir.push(...acoes);
        }
      });

      if (acoesParaInserir.length > 0) {
        console.log('💾 Inserindo ações no banco de dados...', acoesParaInserir.length, 'ações');
        const { error: acoesError } = await supabase
          .from('cp_acoes_cronograma')
          .insert(acoesParaInserir);

        if (acoesError) {
          console.error('❌ Erro ao criar ações:', acoesError);
          throw acoesError;
        }
        console.log('✅ Ações inseridas com sucesso');
      }

      console.log('✅ Fases padrão criadas com sucesso!');
    } catch (err) {
      console.error('❌ Erro ao criar fases padrão:', err);
      setError(err instanceof Error ? err.message : 'Erro ao criar fases padrão');
    }
  }, [user, empresaSelecionada]);

  // Carregar fases do banco de dados
  const carregarFases = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('🔄 Carregando fases do banco de dados...');
      console.log('📊 Usuário:', user.id, user.nome_completo);
      console.log('📊 Empresa selecionada:', empresaSelecionada?.nome, empresaSelecionada?.id);

      // Buscar fases do usuário
      let query = supabase
        .from('cp_fases')
        .select('*')
        .eq('usuario_id', user.id);
      

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== 'SUPER_ADMIN') {
        console.log('🔍 Filtrando por empresa:', empresaSelecionada.nome);
        // Buscar fases da empresa específica
        query = query.eq('empresa_id', empresaSelecionada.id);
      } else {
        console.log('🔍 Carregando todas as fases do usuário');
      }
      const { data: fasesBanco, error: fasesError } = await query.order('ordem');

      if (fasesError) {
        console.error('❌ Erro ao carregar fases:', fasesError);
        throw fasesError;
      }

      /*
      console.log('📊 Fases encontradas no banco:', fasesBanco?.length || 0);
      if (fasesBanco && fasesBanco.length > 0) {
        fasesBanco.forEach((fase, index) => {
          console.log(`📅 Fase ${index + 1}: ${fase.titulo} - ${fase.data_planejada}`);
        });
      }
        */

      // Buscar ações das fases
      let acoesQuery = supabase
        .from('cp_acoes_cronograma')
        .select('*')
        .eq('usuario_id', user.id);
      

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== 'SUPER_ADMIN') {
        console.log('🔍 Filtrando ações por empresa:', empresaSelecionada.nome);
        // Buscar ações da empresa específica
        acoesQuery = acoesQuery.eq('empresa_id', empresaSelecionada.id);
      } else {
        console.log('🔍 Carregando todas as ações do usuário');
      }

      const { data: acoesBanco, error: acoesError } = await acoesQuery.order('ordem');

      if (acoesError) {
        console.error('❌ Erro ao carregar ações:', acoesError);
        throw acoesError;
      }

      console.log('📊 Ações encontradas no banco:', acoesBanco?.length || 0);

      // Se não há fases no banco, criar as fases padrão
      if (!fasesBanco || fasesBanco.length === 0) {
        console.log('📝 Criando fases padrão...');
        await criarFasesPadrao();
        return;
      }

      // Definir a data de término baseada na empresa ou usuário
      const dataTermino = empresaSelecionada?.data_termino_mandato || user.data_termino_mandato || '2025-09-26';
      setDataTerminoMandato(dataTermino);

      // Combinar fases com suas ações
      const fasesComAcoes: FaseComAcoes[] = fasesBanco.map(fase => {
        const acoesDesaFase = acoesBanco?.filter(acao => acao.fase_id === fase.id) || [];
        
        return {
          id: fase.id,
          titulo: fase.titulo,
          data: new Date(fase.data_planejada),
          dataFim: fase.data_fim ? new Date(fase.data_fim) : undefined,
          artigo: fase.artigo_nr || '',
          prazoNR: fase.prazo_nr || '',
          cor: fase.cor || 'bg-blue-500',
          icon: typeof fase.icon === 'string' ? fase.icon : 'Calendar',
          acoes: acoesDesaFase.map(acao => acao.descricao), // Para compatibilidade
          acoes_banco: acoesDesaFase,
          concluida: fase.concluida || false,
          observacoes: fase.observacoes
        };
      });

      console.log('✅ Dados carregados:', { fasesBanco: fasesBanco.length, acoesBanco: acoesBanco?.length || 0 });
      setFases(fasesComAcoes);

    } catch (err) {
      console.error('❌ Erro ao carregar cronograma:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [user, empresaSelecionada]);


  // Marcar/desmarcar ação como concluída
  const toggleAcao = async (faseId: string, acaoIndex: number) => {
    if (!user) return;

    try {
      const fase = fases.find(f => f.id === faseId);
      if (!fase || !fase.acoes_banco[acaoIndex]) return;

      const acao = fase.acoes_banco[acaoIndex];
      const novoConcluida = !acao.concluida;

      let updateQuery = supabase
        .from('cp_acoes_cronograma')
        .update({
          concluida: novoConcluida,
          data_conclusao: novoConcluida ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', acao.id);

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== 'SUPER_ADMIN') {
        updateQuery = updateQuery.eq('empresa_id', empresaSelecionada.id);
      }

      const { error } = await updateQuery;

      if (error) {
        console.error('❌ Erro ao atualizar ação:', error);
        throw error;
      }

      // Atualizar estado local
      setFases(prev => {
        const novasFases = prev.map(f => {
          if (f.id === faseId) {
            const novasAcoes = [...f.acoes_banco];
            novasAcoes[acaoIndex] = {
              ...novasAcoes[acaoIndex],
              concluida: novoConcluida,
              data_conclusao: novoConcluida ? new Date().toISOString() : undefined
            };
            return { ...f, acoes_banco: novasAcoes };
          }
          return f;
        });
        
        return novasFases;
      });

    } catch (err) {
      console.error('❌ Erro ao toggle ação:', err);
      setError(err instanceof Error ? err.message : 'Erro ao atualizar ação');
    }
  };


  // Calcular estatísticas do cronograma
  const getEstatisticas = useCallback(() => {
    const agora = new Date();
    let vencidas = 0;
    let proximoVencimento = 0;
    let concluidas = 0;
    const fasesVencidas: FaseComAcoes[] = [];

    fases.forEach(fase => {
      const dataFase = new Date(fase.data);
      const diasRestantes = Math.ceil((dataFase.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24));
      
      // Verificar se fase está vencida
      if (diasRestantes < 0) {
        vencidas++;
        fasesVencidas.push(fase);
      } else if (diasRestantes <= 7) {
        proximoVencimento++;
      }
      
      // Verificar se fase está concluída (todas as ações marcadas)
      const acoesTotais = fase.acoes_banco.length;
      const acoesConcluidas = fase.acoes_banco.filter(acao => acao.concluida).length;
      
      if (acoesTotais > 0 && acoesConcluidas === acoesTotais) {
        concluidas++;
      }
    });

    return {
      vencidas,
      proximoVencimento,
      concluidas,
      total: fases.length,
      fasesVencidas
    };
  }, [fases]);

  // Recriar cronograma com nova data de término
  const recriarCronograma = useCallback(async (novaDataTermino?: string) => {
    if (!user) return;
    
    try {
      setLoading(true);
      console.log('🔄 Recriando cronograma com nova data...');
      
      // Criar novas fases com a data atualizada
      await criarFasesPadrao(novaDataTermino);
      
      // Recarregar dados
      await carregarFases();
      
    } catch (err) {
      console.error('❌ Erro ao recriar cronograma:', err);
      setError(err instanceof Error ? err.message : 'Erro ao recriar cronograma');
    } finally {
      setLoading(false);
    }
  }, [user, empresaSelecionada]);

  // Carregar dados quando o usuário ou empresa muda
  useEffect(() => {
    if (user) {
      const currentKey = `${user.id}-${empresaSelecionada?.id || 'null'}`;
      console.log('🔄 useEffect carregarFases - user:', user.id, 'empresa:', empresaSelecionada?.id);
      
      // Só carregar se a chave mudou
      if (lastLoadRef.current !== currentKey) {
        lastLoadRef.current = currentKey;
        carregarFases();
      } else {
        console.log('⏭️ Pulando carregamento - mesma chave:', currentKey);
      }
    }
  }, [user, empresaSelecionada, carregarFases]);

  return {
    fases,
    loading,
    error,
    dataTerminoMandato,
    toggleAcao,
    carregarFases,
    recriarCronograma,
    getEstatisticas
  };
};
